/**
 * Sticky-CSS column pinning for the free @mui/x-data-grid (Community) grid —
 * a drop-in replacement for MUI X Pro's `pinnedColumns={{ left: [...] }}`.
 *
 * Builds an `sx` object that makes the pinned columns' header + body cells
 * `position: sticky`, with cumulative left offsets from each column's fixed
 * width. Virtualization must be disabled while pinning is active (Community
 * virtualizes columns horizontally; leftmost pinned cells must stay in the DOM).
 */
import type { GridColDef } from "@mui/x-data-grid"

export type PinnedColumnsModel = { left?: string[]; right?: string[] }

// Backgrounds mirror table.scss exactly so pinned cells match the rest of the row.
const PINNED_BG = "var(--token-bg-surface-1)" // 본문 기본 행 (흰색)
const PINNED_BG_ODD = "var(--token-bg-surface-2)" // odd-row zebra (#F8FAFC)
const PINNED_BG_HOVER = "var(--token-bg-surface-3)" // hover
const PINNED_BG_HEADER = "var(--token-bg-surface-3)" // 헤더 (메트릭 헤더와 동일)
const DEFAULT_COL_WIDTH = 100

function leftOffsets(columns: GridColDef[], left: string[]): Record<string, number> {
  const widthOf = (field: string) => {
    const c = columns.find((col) => col.field === field)

    return c?.width ?? c?.minWidth ?? DEFAULT_COL_WIDTH
  }
  const out: Record<string, number> = {}
  let acc = 0

  for (const field of left) {
    out[field] = acc
    acc += widthOf(field)
  }

  return out
}

export function hasLeftPins(pinned?: PinnedColumnsModel): boolean {
  return (pinned?.left?.length ?? 0) > 0
}

export function buildPinnedSx(columns: GridColDef[], pinned?: PinnedColumnsModel) {
  const left = pinned?.left ?? []

  if (left.length === 0) {
    return {}
  }

  const offsets = leftOffsets(columns, left)
  const sx: Record<string, unknown> = {}

  for (const field of left) {
    const common = {
      position: "sticky" as const,
      left: offsets[field],
    }

    // z-index 는 MUI 네이티브 pinning 과 동일 값(GridRootStyles: 헤더 40 > 컬럼 구분선 30, 셀 30).
    // 이보다 낮으면 스크롤 시 비고정 헤더의 구분선 등이 고정 영역 위로 넘어온다 (WEB-2202)
    sx[`& .MuiDataGrid-columnHeader[data-field="${field}"]`] = {
      ...common,
      zIndex: 40,
      backgroundColor: PINNED_BG_HEADER,
    }
    sx[`& .MuiDataGrid-cell[data-field="${field}"]`] = {
      ...common,
      zIndex: 30,
      backgroundColor: PINNED_BG,
    }
    // total/average(bold-row)는 홀짝 무관 흰색 유지 → odd zebra 에서 제외
    sx[`& .MuiDataGrid-row.odd-row:not(.bold-row) .MuiDataGrid-cell[data-field="${field}"]`] = {
      backgroundColor: PINNED_BG_ODD,
    }
    sx[`& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field="${field}"]`] = {
      backgroundColor: PINNED_BG_HOVER,
    }
  }

  return sx
}
